import { useEffect } from "react";

import axios from "axios";

import { useCurrentWorkspace } from "src/api/queries";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { usePageVisibility } from "src/hooks/usePageVisibility";
import { DATAPLANE_PREWARM_TARGETS } from "src/prewarm/dataplanePrewarmTargets";
import { FEATURE_FLAGS } from "src/router/featureFlags";

/**
 * Let the page's own first burst of requests get out before spending
 * bandwidth and gateway connections on warm-up traffic. The user cannot reach
 * any of these services faster than this anyway: every route that uses one
 * needs at least one navigation or panel open first.
 */
const PREWARM_DELAY_MS = 1_500;

/**
 * An execution environment stays warm for minutes after a request, so
 * re-warming inside this window buys nothing. Bounded per workspace, not per
 * mount, so bouncing between pages in one workspace warms once.
 */
const PREWARM_COOLDOWN_MS = 5 * 60_000;

/**
 * A cold start is exactly what these requests pay for, so the ceiling has to
 * sit well above a warm response time. Past it the container is warm whether
 * or not we are still listening.
 */
const PREWARM_TIMEOUT_MS = 20_000;

const lastPrewarmedAt = new Map<string, number>();

const prewarmWorkspace = (workspaceId: string, wsBaseUrl: string): void => {
  const lastRun = lastPrewarmedAt.get(workspaceId) ?? 0;
  if (Date.now() - lastRun < PREWARM_COOLDOWN_MS) {
    return;
  }
  lastPrewarmedAt.set(workspaceId, Date.now());

  // `allSettled` is what swallows the failures: a workspace that never
  // deployed a module answers 404 and a role without access answers 403, and
  // neither is something the user can act on. Both still ran the Lambda, which
  // is the whole point.
  void Promise.allSettled(
    DATAPLANE_PREWARM_TARGETS.map((target) =>
      axios.get(`https://${wsBaseUrl}${target.path}`, {
        timeout: PREWARM_TIMEOUT_MS,
        validateStatus: () => true,
      }),
    ),
  );
};

/**
 * Warms every workspace data-plane Lambda the UI can reach over GET, once the
 * user is inside a workspace.
 *
 * Fire-and-forget: responses and failures are both discarded. Runs once per
 * workspace per {@link PREWARM_COOLDOWN_MS}, and never from a hidden tab —
 * a tab restored in the background has no user about to click anything.
 */
export const useDataplanePrewarm = (): void => {
  const { data: workspace } = useCurrentWorkspace();
  const pageVisible = usePageVisibility();
  const disabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.noDataplanePrewarm);

  const workspaceId = workspace?.id;
  const wsBaseUrl = workspace?.base_url;

  useEffect(() => {
    if (disabled || !pageVisible || !workspaceId || !wsBaseUrl) {
      return;
    }

    const timer = setTimeout(
      () => prewarmWorkspace(workspaceId, wsBaseUrl),
      PREWARM_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [disabled, pageVisible, workspaceId, wsBaseUrl]);
};

/** Reset module-level state for testing purposes. */
export const __resetDataplanePrewarmState = (): void => {
  lastPrewarmedAt.clear();
};
